import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import Modal from "@mui/material/Modal";
import IconButton from "@mui/material/IconButton";
import * as AddCircleOutlineIconModule from "@mui/icons-material/AddCircleOutline.js";
import * as ScienceIconModule from "@mui/icons-material/Science.js";
import * as ContentCopyIconModule from "@mui/icons-material/ContentCopy.js";
import * as DeleteIconModule from "@mui/icons-material/Delete.js";
import Page from "../Components/Page";
import MainButton from "../Components/Buttons/MainButton";
import DeleteResourceModal from "../Components/DeleteResourceModal";
import ModalShell, { ModalTitle } from "../Components/ModalShell";
import VersionHistoryControl, {
  HistoryViewingBanner,
} from "../Components/VersionHistoryControl";
import { useAuth } from "../Components/Auth/useAuth";
import useVersionHistory from "../hooks/useVersionHistory";
import { axiosInstance, buildLegacyHeaders } from "../Components/axiosFetchers";
import { buildSparsePayload, toOptionalNumber } from "../utils/sparsePayload";
import { hasComparableFormChanges } from "../utils/formComparison";
import { duplicateNameMessage } from "../utils/apiErrors.js";
import {
  GrowerToolbar,
  WorkspaceState,
} from "../Components/GrowerLayout.jsx";
import { trackEvent } from "../analytics/ga4.js";

const FERTILIZER_BASE_PATH = "/fertilizers";
const AddCircleOutlineIcon = AddCircleOutlineIconModule.default;
const ScienceIcon = ScienceIconModule.default;
const ContentCopyIcon = ContentCopyIconModule.default;
const DeleteIcon = DeleteIconModule.default;
function normalizeIngredientForm(ingredient) {
  return {
    name: ingredient?.name || "",
    n_value: ingredient?.n_value === 0 || ingredient?.n_value ? ingredient.n_value : 0,
    p_value: ingredient?.p_value === 0 || ingredient?.p_value ? ingredient.p_value : 0,
    k_value: ingredient?.k_value === 0 || ingredient?.k_value ? ingredient.k_value : 0,
    s_value: ingredient?.s_value === 0 || ingredient?.s_value ? ingredient.s_value : 0,
  };
}

function normalizeMixForm(mix) {
  const ingredientRows = Array.isArray(mix?.ingredients)
    ? mix.ingredients
    : Array.isArray(mix?.ingredients_snapshot)
      ? mix.ingredients_snapshot
      : [];

  return {
    name: mix?.name || "",
    ingredients: ingredientRows.length
      ? ingredientRows.map((row) => ({
          ingredient_id: row.ingredient_id || "",
          amount_in_pounds:
            row.amount_in_pounds === 0 || row.amount_in_pounds
              ? row.amount_in_pounds
              : "",
        }))
      : [{ ingredient_id: "", amount_in_pounds: "" }],
  };
}

function buildMixPayload(mix) {
  return buildSparsePayload({
    name: mix?.name,
    ingredients: normalizeMixForm(mix).ingredients.map((row) => ({
      ingredient_id: row.ingredient_id,
      amount_in_pounds: toOptionalNumber(row.amount_in_pounds),
    })),
  });
}

function buildIngredientPayload(ingredient) {
  return buildSparsePayload({
    name: ingredient?.name,
    n_value: toOptionalNumber(ingredient?.n_value) ?? 0,
    p_value: toOptionalNumber(ingredient?.p_value) ?? 0,
    k_value: toOptionalNumber(ingredient?.k_value) ?? 0,
    s_value: toOptionalNumber(ingredient?.s_value) ?? 0,
  });
}

function upsertById(items, nextItem) {
  const withoutCurrent = items.filter((item) => item.id !== nextItem.id);
  return [...withoutCurrent, nextItem];
}

function precise(number) {
  return Math.round(number * 100) / 100;
}

function computeMixTotals(rows, ingredientMap) {
  const totalPounds = rows.reduce((sum, row) => {
    const amount = Number(row.amount_in_pounds);
    return sum + (Number.isFinite(amount) ? amount : 0);
  }, 0);

  const totals = { N: 0, P: 0, K: 0, S: 0 };

  rows.forEach((row) => {
    const ingredient = ingredientMap[row.ingredient_id];
    const amount = Number(row.amount_in_pounds);

    if (!ingredient || !Number.isFinite(amount) || totalPounds <= 0) {
      return;
    }

    totals.N += Number(ingredient.n_value || 0) * (amount / totalPounds);
    totals.P += Number(ingredient.p_value || 0) * (amount / totalPounds);
    totals.K += Number(ingredient.k_value || 0) * (amount / totalPounds);
    totals.S += Number(ingredient.s_value || 0) * (amount / totalPounds);
  });

  return {
    totalPounds: precise(totalPounds),
    proxyNpks: `${precise(totals.N)}-${precise(totals.P)}-${precise(
      totals.K
    )}-${precise(totals.S)}`,
    totalsLbAc: {
      N: precise((totals.N * totalPounds) / 100),
      P: precise((totals.P * totalPounds) / 100),
      K: precise((totals.K * totalPounds) / 100),
      S: precise((totals.S * totalPounds) / 100),
    },
  };
}

function IngredientEditorModal({
  formState,
  saveDisabled = false,
  onChange,
  onClose,
  onSave,
  open,
  saving,
  title,
}) {
  return (
    <Modal onClose={onClose} open={open}>
      <ModalShell
        accent="info"
        icon={<ScienceIcon sx={{ width: "38px", height: "38px", color: "#FFFFFF" }} />}
        onClose={onClose}
      >
          <ModalTitle accent="info">{title}</ModalTitle>
          <div className="grid gap-4 md:grid-cols-2">
            <TextField
              fullWidth
              label="Ingredient Name"
              value={formState.name}
              onChange={(event) => onChange("name", event.target.value)}
            />
            <TextField
              fullWidth
              label="Nitrogen"
              type="number"
              value={formState.n_value}
              onChange={(event) => onChange("n_value", event.target.value)}
            />
            <TextField
              fullWidth
              label="Phosphorus"
              type="number"
              value={formState.p_value}
              onChange={(event) => onChange("p_value", event.target.value)}
            />
            <TextField
              fullWidth
              label="Potassium"
              type="number"
              value={formState.k_value}
              onChange={(event) => onChange("k_value", event.target.value)}
            />
            <TextField
              fullWidth
              label="Sulphur"
              type="number"
              value={formState.s_value}
              onChange={(event) => onChange("s_value", event.target.value)}
            />
          </div>
          <div className="mt-6 flex justify-center gap-4">
            <MainButton
              onClick={onSave}
              text={saving ? "Saving..." : "Save Ingredient"}
              disabled={saving || saveDisabled}
            />
            <MainButton onClick={onClose} secondary={true} text="Cancel" />
          </div>
      </ModalShell>
    </Modal>
  );
}

function FertilizerMixCard({ mix }) {
  return (
    <Link
      className="rounded-[8px] border border-slate-200 bg-white p-5 shadow-[0_18px_45px_rgba(15,23,42,0.05)] transition hover:-translate-y-0.5 hover:shadow-[0_22px_55px_rgba(15,23,42,0.08)]"
      to={`${FERTILIZER_BASE_PATH}/${mix.id}`}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex h-14 w-14 items-center justify-center rounded-[8px] bg-[#eef7fb] text-[#243245]">
          <ScienceIcon sx={{ fontSize: 28 }} />
        </div>
        <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-[12px] font-bold uppercase tracking-[0.16em] text-slate-500">
          {mix.ingredients?.length || 0} ingredients
        </span>
      </div>
      <h3 className="mt-5 text-[24px] font-black text-slate-900">
        {mix.name || "Untitled mix"}
      </h3>
      <div className="mt-5 grid gap-3 sm:grid-cols-2">
        <div className="rounded-[8px] border border-slate-200 bg-slate-50 px-4 py-3">
          <p className="text-[12px] font-bold uppercase tracking-[0.16em] text-slate-400">
            Proxy NPKS
          </p>
          <p className="mt-2 text-lg font-bold text-slate-900">
            {mix.totals?.proxy_npks || "Not returned"}
          </p>
        </div>
        <div className="rounded-[8px] border border-slate-200 bg-slate-50 px-4 py-3">
          <p className="text-[12px] font-bold uppercase tracking-[0.16em] text-slate-400">
            Total pounds
          </p>
          <p className="mt-2 text-lg font-bold text-slate-900">
            {mix.ingredients?.reduce(
              (sum, row) => sum + Number(row.amount_in_pounds || 0),
              0
            ) || "Not set"}
          </p>
        </div>
      </div>
    </Link>
  );
}

function IngredientLibraryTable({
  ingredients,
  onCreate,
  onDelete,
  onEdit,
  readOnly = false,
}) {
  return (
    <section className="flex h-full flex-col border-t border-slate-200 pt-5 xl:border-l xl:border-t-0 xl:pl-6 xl:pt-0">
      <div>
        <div>
          <h3 className="text-lg font-black text-slate-900">Ingredient Library</h3>
          <p className="mt-2 text-[15px] leading-7 text-slate-600">
            Manage reusable nutrient ingredients here. System ingredients are visible
            but remain read-only.
          </p>
        </div>
      </div>

      <div className="mt-5 flex-1 overflow-hidden">
        <table className="w-full table-fixed text-left text-sm">
          <thead className="bg-slate-50 text-slate-500">
            <tr>
              <th className="w-[34%] px-3 py-3 font-bold">Name</th>
              <th className="w-[11%] px-2 py-3 font-bold">N</th>
              <th className="w-[11%] px-2 py-3 font-bold">P</th>
              <th className="w-[11%] px-2 py-3 font-bold">K</th>
              <th className="w-[11%] px-2 py-3 font-bold">S</th>
              <th className="w-[22%] px-3 py-3 font-bold text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {ingredients.map((ingredient) => {
              const isSystem = ingredient.owner_email === "admin";
              const canEdit = !isSystem && !readOnly;

              return (
                <tr
                  className={`border-t border-slate-200 ${
                    canEdit ? "cursor-pointer transition hover:bg-slate-50" : ""
                  }`}
                  key={ingredient.id}
                  onClick={canEdit ? () => onEdit(ingredient) : undefined}
                >
                  <td className="px-3 py-3 font-semibold text-slate-800">
                    {ingredient.name}
                  </td>
                  <td className="px-2 py-3 text-slate-600">{ingredient.n_value}</td>
                  <td className="px-2 py-3 text-slate-600">{ingredient.p_value}</td>
                  <td className="px-2 py-3 text-slate-600">{ingredient.k_value}</td>
                  <td className="px-2 py-3 text-slate-600">{ingredient.s_value}</td>
                  <td className="px-3 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <IconButton
                        disabled={isSystem || readOnly}
                        onClick={(event) => {
                          event.stopPropagation();
                          onDelete(ingredient);
                        }}
                        size="small"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="mt-5 flex items-end">
        <MainButton
          disabled={readOnly}
          onClick={onCreate}
          text="Add Ingredient"
        />
      </div>
    </section>
  );
}

function FertilizerDetailPage({
  deleteOpen,
  formState,
  historyBanner = null,
  historyControl = null,
  ingredientMap,
  ingredients,
  ingredientEditor,
  ingredientEditorState,
  ingredientSaving,
  isSaveDisabled = false,
  ingredientSaveDisabled = false,
  isReadOnly = false,
  mix,
  mixId,
  onAddIngredientRow,
  onChangeIngredientEditor,
  onCloseIngredientEditor,
  onDeleteMix,
  onDeleteRow,
  onOpenIngredientDelete,
  onOpenIngredientEditor,
  onSaveIngredientEditor,
  onSaveMix,
  onUpdateRow,
  saveState,
  setDeleteOpen,
}) {
  const navigate = useNavigate();
  const isNew = mixId === "new";
  const computedTotals = useMemo(
    () => computeMixTotals(formState.ingredients, ingredientMap),
    [formState.ingredients, ingredientMap]
  );

  return (
    <div className="min-h-full w-full bg-[#fbfbf8] px-4 py-5 md:px-8">
      <div className="mx-auto w-full max-w-[1240px]">
        <div className="rounded-[8px] border border-slate-200 bg-white shadow-[0_24px_60px_rgba(18,28,45,0.08)]">
          <section className="overflow-hidden rounded-t-[8px] border-b border-slate-200 bg-[linear-gradient(135deg,#eef7fb_0%,#ffffff_45%,#fdf6ef_100%)] px-6 py-7 md:px-10 md:py-9">
            <div className="flex flex-col gap-8 lg:flex-row lg:items-start lg:justify-between">
              <div className="max-w-[760px]">
                <div className="flex flex-wrap items-center gap-3 text-[12px] font-bold uppercase tracking-[0.2em] text-slate-500">
                  <Link className="text-[#243245] hover:text-[#f15d22]" to={FERTILIZER_BASE_PATH}>
                    Fertilizers
                  </Link>
                  <span>&bull;</span>
                  <span>{isNew ? "New mix" : "Mix detail"}</span>
                </div>
                <h1 className="mt-5 text-[34px] font-black leading-tight text-slate-900 md:text-[56px]">
                  {isNew ? "Create Fertilizer Mix" : mix?.name || "Fertilizer Mix"}
                </h1>
                <p className="mt-4 max-w-[700px] text-[18px] leading-8 text-slate-600">
                  Build reusable fertilizer mixes here using the same ingredient and
                  pounds-applied model used inside the crop year 4R workflow.
                </p>
                <div className="mt-6">{historyBanner}</div>
                <div className="mt-6 flex flex-wrap items-center gap-3">
                  <MainButton
                    onClick={onSaveMix}
                    text={saveState === "saving" ? "Saving..." : isNew ? "Create Mix" : "Save Changes"}
                    disabled={saveState === "saving" || isReadOnly || isSaveDisabled}
                  />
                  <MainButton
                    onClick={() => navigate(FERTILIZER_BASE_PATH)}
                    secondary={true}
                    text="Back to list"
                  />
                  {!isNew && !isReadOnly && (
                    <MainButton
                      destructive={true}
                      text="Delete Mix"
                      onClick={() => setDeleteOpen(true)}
                    />
                  )}
                </div>
              </div>
              <div className="w-full max-w-[340px] shrink-0 space-y-4">
                <div className="flex justify-end">{historyControl}</div>
                <div className="rounded-[8px] border border-slate-200 bg-white/80 px-5 py-4 shadow-[0_16px_30px_rgba(20,28,38,0.05)]">
                  <div className="text-[12px] font-bold uppercase tracking-[0.18em] text-slate-400">
                    Proxy NPKS
                  </div>
                  <div className="mt-2 text-2xl font-black text-slate-900">
                    {mix?.totals?.proxy_npks || computedTotals.proxyNpks}
                  </div>
                </div>
                <div className="rounded-[8px] border border-slate-200 bg-white/80 px-5 py-4 shadow-[0_16px_30px_rgba(20,28,38,0.05)]">
                  <div className="text-[12px] font-bold uppercase tracking-[0.18em] text-slate-400">
                    Total pounds
                  </div>
                  <div className="mt-2 text-2xl font-black text-slate-900">
                    {computedTotals.totalPounds}
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="grid gap-8 px-6 py-8 md:px-10 lg:grid-cols-[minmax(0,1fr)_360px]">
            <div className="space-y-8">
              <div className="rounded-[8px] border border-slate-200 bg-white p-6">
                <h2 className="text-[22px] font-black text-slate-900">Mix Definition</h2>
                <div className="mt-6">
                  <TextField
                    disabled={isReadOnly}
                    fullWidth
                    label="Mix Name"
                    value={formState.name}
                    onChange={(event) =>
                      onUpdateRow("name", event.target.value, true)
                    }
                  />
                </div>

                <div className="mt-6 overflow-x-auto">
                  <table className="min-w-full text-left text-sm">
                    <thead className="bg-slate-50 text-slate-500">
                      <tr>
                        <th className="px-3 py-3 font-bold">Ingredient</th>
                        <th className="px-3 py-3 font-bold">Amount Applied (lbs)</th>
                        <th className="px-3 py-3 font-bold">NPKS</th>
                        <th className="px-3 py-3 font-bold">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {formState.ingredients.map((row, index) => {
                        const ingredient = ingredientMap[row.ingredient_id];

                        return (
                          <tr className="border-t border-slate-200" key={`mix-row-${index}`}>
                            <td className="px-3 py-3">
                              <TextField
                                disabled={isReadOnly}
                                fullWidth
                                select
                                label="Ingredient"
                                value={row.ingredient_id}
                                onChange={(event) =>
                                  onUpdateRow(index, {
                                    ...row,
                                    ingredient_id: event.target.value,
                                  })
                                }
                              >
                                {ingredients.map((option) => (
                                  <MenuItem key={option.id} value={option.id}>
                                    {option.name}
                                  </MenuItem>
                                ))}
                              </TextField>
                            </td>
                            <td className="px-3 py-3">
                              <TextField
                                disabled={isReadOnly}
                                fullWidth
                                label="Amount"
                                type="number"
                                value={row.amount_in_pounds}
                                onChange={(event) =>
                                  onUpdateRow(index, {
                                    ...row,
                                    amount_in_pounds: event.target.value,
                                  })
                                }
                                InputProps={{ inputProps: { min: 0, step: 0.1 } }}
                              />
                            </td>
                            <td className="px-3 py-3 text-slate-600">
                              {ingredient
                                ? `${ingredient.n_value}-${ingredient.p_value}-${ingredient.k_value}-${ingredient.s_value}`
                                : "Select ingredient"}
                            </td>
                            <td className="px-3 py-3">
                              <IconButton
                                disabled={isReadOnly}
                                onClick={() => onDeleteRow(index)}
                                size="small"
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                <div className="mt-5">
                  <Button
                    disabled={isReadOnly}
                    onClick={onAddIngredientRow}
                    startIcon={<AddCircleOutlineIcon />}
                    sx={{ textTransform: "none" }}
                  >
                    Add Ingredient Row
                  </Button>
                </div>
              </div>

              <div className="rounded-[8px] border border-slate-200 bg-white p-6">
                <h2 className="text-[22px] font-black text-slate-900">Computed Summary</h2>
                <div className="mt-5 grid gap-4 md:grid-cols-2">
                  <div className="rounded-[8px] border border-slate-200 bg-slate-50 px-4 py-4">
                    <p className="text-[12px] font-bold uppercase tracking-[0.16em] text-slate-400">
                      Proxy NPKS
                    </p>
                    <p className="mt-2 text-2xl font-black text-slate-900">
                      {computedTotals.proxyNpks}
                    </p>
                  </div>
                  <div className="rounded-[8px] border border-slate-200 bg-slate-50 px-4 py-4">
                    <p className="text-[12px] font-bold uppercase tracking-[0.16em] text-slate-400">
                      Total pounds
                    </p>
                    <p className="mt-2 text-2xl font-black text-slate-900">
                      {computedTotals.totalPounds}
                    </p>
                  </div>
                </div>
                <div className="mt-5 grid gap-4 md:grid-cols-4">
                  {Object.entries(computedTotals.totalsLbAc).map(([key, value]) => (
                    <div
                      className="rounded-[8px] border border-slate-200 bg-white px-4 py-4"
                      key={key}
                    >
                      <p className="text-[12px] font-bold uppercase tracking-[0.16em] text-slate-400">
                        {key} nutrient pounds
                      </p>
                      <p className="mt-2 text-xl font-black text-slate-900">{value}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="space-y-5">
              <IngredientLibraryTable
                ingredients={ingredients}
                onCreate={() => onOpenIngredientEditor(null)}
                onDelete={onOpenIngredientDelete}
                onEdit={onOpenIngredientEditor}
                readOnly={isReadOnly}
              />
            </div>
          </section>
        </div>
      </div>

      <IngredientEditorModal
        formState={ingredientEditorState}
        onChange={onChangeIngredientEditor}
        onClose={onCloseIngredientEditor}
        onSave={onSaveIngredientEditor}
        open={ingredientEditor.open}
        saving={ingredientSaving}
        saveDisabled={ingredientSaveDisabled}
        title={ingredientEditor.id ? "Edit Ingredient" : "Create Ingredient"}
      />
      <DeleteResourceModal
        title="Delete Fertilizer Mix"
        description={`Are you sure you wish to permanently delete the ${
          mix?.name || "selected"
        } fertilizer mix?`}
        open={deleteOpen}
        handleClose={setDeleteOpen}
        handleDelete={onDeleteMix}
        resourceId={mix?.id}
      />
    </div>
  );
}

export default function FertilizersPage() {
  const { mixId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [mixes, setMixes] = useState([]);
  const [ingredients, setIngredients] = useState([]);
  const [mixForm, setMixForm] = useState(normalizeMixForm());
  const [currentMix, setCurrentMix] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saveState, setSaveState] = useState("idle");
  const [pageError, setPageError] = useState("");
  const [sortBy, setSortBy] = useState("name");
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [ingredientDeleteTarget, setIngredientDeleteTarget] = useState(null);
  const [ingredientEditor, setIngredientEditor] = useState({
    open: false,
    id: null,
  });
  const [ingredientEditorState, setIngredientEditorState] = useState(
    normalizeIngredientForm()
  );
  const [ingredientEditorBaseline, setIngredientEditorBaseline] = useState(
    normalizeIngredientForm()
  );
  const [ingredientSaving, setIngredientSaving] = useState(false);
  const history = useVersionHistory({
    user,
    enabled: Boolean(mixId) && mixId !== "new",
    historyPath:
      mixId && mixId !== "new"
        ? `/assessments/fertilizer_mixes/${mixId}`
        : "",
    currentData: currentMix,
    onRestore: async (snapshot) => {
      const response = await axiosInstance.patch(
        `${process.env.REACT_APP_API_URL}/assessments/fertilizer_mixes/${mixId}`,
        buildMixPayload(snapshot),
        {
          headers: buildLegacyHeaders(user),
        }
      );

      return response.data;
    },
    onRestoreSuccess: (savedMix) => {
      setCurrentMix(savedMix);
      setMixForm(normalizeMixForm(savedMix));
      setMixes((prevState) => upsertById(prevState, savedMix));
    },
  });
  const displayedMix = history.versionData || currentMix;
  const displayedMixForm = history.isViewingHistoricalVersion
    ? normalizeMixForm(displayedMix)
    : mixForm;

  document.title = "Fertilizers - Field To Market Canada";

  const ingredientMap = useMemo(
    () =>
      ingredients.reduce((map, ingredient) => {
        map[ingredient.id] = ingredient;
        return map;
      }, {}),
    [ingredients]
  );
  const savedMixForm = useMemo(() => normalizeMixForm(currentMix), [currentMix]);
  const isMixSaveDisabled = useMemo(
    () =>
      !hasComparableFormChanges(
        buildMixPayload(mixForm),
        buildMixPayload(savedMixForm)
      ),
    [mixForm, savedMixForm]
  );
  const isIngredientEditorSaveDisabled = useMemo(
    () =>
      !hasComparableFormChanges(
        buildIngredientPayload(ingredientEditorState),
        buildIngredientPayload(ingredientEditorBaseline)
      ),
    [ingredientEditorBaseline, ingredientEditorState]
  );

  useEffect(() => {
    let isMounted = true;

    async function loadData() {
      setLoading(true);
      setPageError("");

      try {
        const [mixesResponse, ingredientsResponse] = await Promise.all([
          axiosInstance.get(
            `${process.env.REACT_APP_API_URL}/assessments/fertilizer_mixes`,
            {
              headers: buildLegacyHeaders(user),
            }
          ),
          axiosInstance.get(
            `${process.env.REACT_APP_API_URL}/assessments/fertilizer_ingredients`,
            {
              headers: buildLegacyHeaders(user),
            }
          ),
        ]);

        if (!isMounted) {
          return;
        }

        const nextMixes = Array.isArray(mixesResponse.data) ? mixesResponse.data : [];
        const nextIngredients = Array.isArray(ingredientsResponse.data)
          ? ingredientsResponse.data
          : [];

        setMixes(nextMixes);
        setIngredients(nextIngredients);

        if (!mixId) {
          setCurrentMix(null);
          setMixForm(normalizeMixForm());
          return;
        }

        if (mixId === "new") {
          setCurrentMix(null);
          setMixForm(normalizeMixForm());
          return;
        }

        const detailResponse = await axiosInstance.get(
          `${process.env.REACT_APP_API_URL}/assessments/fertilizer_mixes/${mixId}`,
          {
            headers: buildLegacyHeaders(user),
          }
        );

        if (!isMounted) {
          return;
        }

        setCurrentMix(detailResponse.data);
        setMixForm(normalizeMixForm(detailResponse.data));
      } catch (error) {
        console.error(error);
        if (isMounted) {
          setPageError("We couldn't load the fertilizer library right now.");
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    }

    loadData();

    return () => {
      isMounted = false;
    };
  }, [mixId, user]);

  const visibleMixes = useMemo(() => {
    const nextMixes = [...mixes];
    nextMixes.sort((left, right) => {
      if (sortBy === "recent") {
        return (
          new Date(right.updated_at || right.created_at || 0).getTime() -
          new Date(left.updated_at || left.created_at || 0).getTime()
        );
      }

      if (sortBy === "ingredients") {
        return (right.ingredients?.length || 0) - (left.ingredients?.length || 0);
      }

      return String(left.name || "").localeCompare(String(right.name || ""));
    });
    return nextMixes;
  }, [mixes, sortBy]);

  function handleUpdateMixField(indexOrKey, nextValue, topLevel = false) {
    setSaveState("idle");
    setPageError("");

    if (topLevel) {
      setMixForm((prevState) => ({ ...prevState, [indexOrKey]: nextValue }));
      return;
    }

    setMixForm((prevState) => {
      const nextIngredients = [...prevState.ingredients];
      nextIngredients[indexOrKey] = nextValue;
      return {
        ...prevState,
        ingredients: nextIngredients,
      };
    });
  }

  function handleAddIngredientRow() {
    setSaveState("idle");
    setMixForm((prevState) => ({
      ...prevState,
      ingredients: [
        ...prevState.ingredients,
        { ingredient_id: "", amount_in_pounds: "" },
      ],
    }));
  }

  function handleDeleteIngredientRow(index) {
    setSaveState("idle");
    setMixForm((prevState) => {
      const nextIngredients = prevState.ingredients.filter(
        (_, rowIndex) => rowIndex !== index
      );
      return {
        ...prevState,
        ingredients:
          nextIngredients.length > 0
            ? nextIngredients
            : [{ ingredient_id: "", amount_in_pounds: "" }],
      };
    });
  }

  async function handleSaveMix() {
    setSaveState("saving");
    setPageError("");

    try {
      const payload = buildMixPayload(mixForm);

      const response =
        mixId && mixId !== "new"
          ? await axiosInstance.patch(
              `${process.env.REACT_APP_API_URL}/assessments/fertilizer_mixes/${mixId}`,
              payload,
              {
                headers: buildLegacyHeaders(user),
              }
            )
          : await axiosInstance.post(
              `${process.env.REACT_APP_API_URL}/assessments/fertilizer_mixes`,
              payload,
              {
                headers: buildLegacyHeaders(user),
              }
            );

      const savedMix = response.data;
      setMixes((prevState) => upsertById(prevState, savedMix));
      setCurrentMix(savedMix);
      setMixForm(normalizeMixForm(savedMix));
      trackEvent("fertilizer_mix_saved", {
        workflow_area: "fertilizers",
        is_new_record: !mixId || mixId === "new",
        result: "success",
      });
      setSaveState("saved");

      if (!mixId || mixId === "new") {
        navigate(`${FERTILIZER_BASE_PATH}/${savedMix.id}`, { replace: true });
      }
    } catch (error) {
      console.error(error);
      setSaveState("error");
      setPageError(
        duplicateNameMessage(error, {
          resourceLabel: "fertilizer mix",
          name: mixForm.name,
          fallback: "We couldn't save this fertilizer mix.",
        })
      );
    }
  }

  async function handleDeleteMix() {
    if (!currentMix?.id) {
      return;
    }

    try {
      await axiosInstance.delete(
        `${process.env.REACT_APP_API_URL}/assessments/fertilizer_mixes/${currentMix.id}`,
        {
          headers: buildLegacyHeaders(user),
        }
      );

      setMixes((prevState) => prevState.filter((mix) => mix.id !== currentMix.id));
      setDeleteOpen(false);
      navigate(FERTILIZER_BASE_PATH);
    } catch (error) {
      console.error(error);
      setPageError("We couldn't delete this fertilizer mix.");
      setDeleteOpen(false);
    }
  }

  function openIngredientEditor(ingredient) {
    const nextState = normalizeIngredientForm(ingredient);
    setIngredientEditor({
      open: true,
      id: ingredient?.id || null,
    });
    setIngredientEditorState(nextState);
    setIngredientEditorBaseline(nextState);
  }

  function closeIngredientEditor() {
    setIngredientEditor({ open: false, id: null });
    setIngredientEditorState(normalizeIngredientForm());
    setIngredientEditorBaseline(normalizeIngredientForm());
  }

  function handleChangeIngredientEditor(key, value) {
    setPageError("");
    setIngredientEditorState((prevState) => ({
      ...prevState,
      [key]: value,
    }));
  }

  async function handleSaveIngredientEditor() {
    setIngredientSaving(true);
    setPageError("");

    try {
      const payload = buildIngredientPayload(ingredientEditorState);

      const response = ingredientEditor.id
        ? await axiosInstance.patch(
            `${process.env.REACT_APP_API_URL}/assessments/fertilizer_ingredients/${ingredientEditor.id}`,
            payload,
            {
              headers: buildLegacyHeaders(user),
            }
          )
        : await axiosInstance.post(
            `${process.env.REACT_APP_API_URL}/assessments/fertilizer_ingredients`,
            payload,
            {
              headers: buildLegacyHeaders(user),
            }
          );

      const savedIngredient = response.data;
      setIngredients((prevState) => {
        const withoutCurrent = prevState.filter(
          (ingredient) => ingredient.id !== savedIngredient.id
        );
        return [...withoutCurrent, savedIngredient].sort((left, right) =>
          String(left.name || "").localeCompare(String(right.name || ""))
        );
      });

      closeIngredientEditor();
    } catch (error) {
      console.error(error);
      setPageError(
        duplicateNameMessage(error, {
          resourceLabel: "fertilizer ingredient",
          name: ingredientEditorState.name,
          fallback: "We couldn't save this fertilizer ingredient.",
        })
      );
    } finally {
      setIngredientSaving(false);
    }
  }

  async function handleDeleteIngredient() {
    if (!ingredientDeleteTarget?.id) {
      return;
    }

    try {
      await axiosInstance.delete(
        `${process.env.REACT_APP_API_URL}/assessments/fertilizer_ingredients/${ingredientDeleteTarget.id}`,
        {
          headers: buildLegacyHeaders(user),
        }
      );

      setIngredients((prevState) =>
        prevState.filter(
          (ingredient) => ingredient.id !== ingredientDeleteTarget.id
        )
      );
      setIngredientDeleteTarget(null);
    } catch (error) {
      console.error(error);
      setPageError("We couldn't delete this fertilizer ingredient.");
      setIngredientDeleteTarget(null);
    }
  }

  if (mixId) {
    return (
      <Page
        title="Fertilizers"
        headerBorderColor="border-[#34a853]"
        headerVariant="hybrid"
        contentWidth="wide"
        padding="p-0"
        introContent={
          <p>
            Manage reusable fertilizer mixes and ingredients globally so the same
            definitions are available across 4R workflows and crop year entry.
          </p>
        }
      >
        <>
          <FertilizerDetailPage
            deleteOpen={deleteOpen}
            formState={displayedMixForm}
            historyBanner={
              <HistoryViewingBanner
                onOpenHistory={history.openDialog}
                onRestore={history.restoreSelectedVersion}
                onShowCurrent={history.showCurrentVersion}
                onViewNewer={
                  history.canViewNewer
                    ? () => history.selectAdjacentVersion(-1)
                    : undefined
                }
                onViewOlder={
                  history.canViewOlder
                    ? () => history.selectAdjacentVersion(1)
                    : undefined
                }
                restoreDisabled={history.restoreState === "saving"}
                restoring={history.restoreState === "saving"}
                summary={history.activeSummary}
              />
            }
            historyControl={
              mixId !== "new" ? (
                <VersionHistoryControl
                  activeSummary={history.activeSummary}
                  canViewNewer={history.canViewNewer}
                  canViewOlder={history.canViewOlder}
                  detailState={history.detailState}
                  dialogOpen={history.dialogOpen}
                  errorMessage={history.errorMessage}
                  onClose={history.closeDialog}
                  onOpen={history.openDialog}
                  onRestore={history.restoreSelectedVersion}
                  onSelectVersion={history.selectVersion}
                  onShowCurrent={history.showCurrentVersion}
                  onViewNewer={
                    history.canViewNewer
                      ? () => history.selectAdjacentVersion(-1)
                      : undefined
                  }
                  onViewOlder={
                    history.canViewOlder
                      ? () => history.selectAdjacentVersion(1)
                      : undefined
                  }
                  resourceLabel="Fertilizer Mix"
                  restoreState={history.restoreState}
                  summaries={history.summaries}
                  summaryState={history.summaryState}
                  summaryTitle={(summary) =>
                    summary?.name || `Fertilizer Mix v${summary?.version_no || "-"}`
                  }
                />
              ) : null
            }
            ingredientMap={ingredientMap}
            ingredients={ingredients}
            ingredientEditor={ingredientEditor}
            ingredientEditorState={ingredientEditorState}
            ingredientSaving={ingredientSaving}
            ingredientSaveDisabled={isIngredientEditorSaveDisabled}
            isSaveDisabled={isMixSaveDisabled}
            isReadOnly={history.isViewingHistoricalVersion}
            mix={displayedMix}
            mixId={mixId}
            onAddIngredientRow={handleAddIngredientRow}
            onChangeIngredientEditor={handleChangeIngredientEditor}
            onCloseIngredientEditor={closeIngredientEditor}
            onDeleteMix={handleDeleteMix}
            onDeleteRow={handleDeleteIngredientRow}
            onOpenIngredientDelete={setIngredientDeleteTarget}
            onOpenIngredientEditor={openIngredientEditor}
            onSaveIngredientEditor={handleSaveIngredientEditor}
            onSaveMix={handleSaveMix}
            onUpdateRow={handleUpdateMixField}
            saveState={saveState}
            setDeleteOpen={setDeleteOpen}
          />
          {pageError ? (
            <div className="px-8 pb-6 text-sm text-[#c62828]">{pageError}</div>
          ) : null}
          <DeleteResourceModal
            title="Delete Ingredient"
            description={`Are you sure you wish to permanently delete the ${
              ingredientDeleteTarget?.name || "selected"
            } ingredient?`}
            open={Boolean(ingredientDeleteTarget)}
            handleClose={() => setIngredientDeleteTarget(null)}
            handleDelete={handleDeleteIngredient}
            resourceId={ingredientDeleteTarget?.id}
          />
        </>
      </Page>
    );
  }

  return (
    <Page
      title="Fertilizers"
      headerBorderColor="border-[#34a853]"
      headerVariant="hybrid"
      contentWidth="wide"
      padding="px-4 pb-6 pt-5 md:px-6 md:pb-8"
      introContent={
        <p>
          Manage reusable fertilizer mixes and ingredients globally so the same
          definitions are available across 4R workflows and crop year entry.
        </p>
      }
      headerActions={
        <div className="flex flex-wrap gap-3">
          <MainButton
            onClick={() => navigate(`${FERTILIZER_BASE_PATH}/new`)}
            text="Create Mix"
          />
          <MainButton
            onClick={() => openIngredientEditor(null)}
            secondary={true}
            text="Add Ingredient"
          />
        </div>
      }
    >
      <div className="w-full text-[#666666]">
        <section className="w-full">
          <GrowerToolbar>
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <TextField
                select
                label="Sort mixes"
                value={sortBy}
                onChange={(event) => setSortBy(event.target.value)}
                sx={{ minWidth: 240 }}
              >
                <MenuItem value="name">Name</MenuItem>
                <MenuItem value="recent">Recently updated</MenuItem>
                <MenuItem value="ingredients">Most ingredients</MenuItem>
              </TextField>
              <div className="flex flex-wrap items-center gap-3 text-sm text-slate-500">
                <span className="inline-flex items-center gap-2">
                  <ScienceIcon sx={{ fontSize: 18 }} />
                  {visibleMixes.length} mixes
                </span>
                <span className="inline-flex items-center gap-2">
                  <ContentCopyIcon sx={{ fontSize: 18 }} />
                  {ingredients.length} ingredients
                </span>
              </div>
            </div>
          </GrowerToolbar>

          {pageError ? (
            <p className="mt-4 text-sm text-[#c62828]">{pageError}</p>
          ) : null}

          <div className="mt-6 grid gap-8 xl:grid-cols-[minmax(0,1fr)_380px]">
            <div>
              {loading ? (
                <WorkspaceState state="loading" title="Loading fertilizer mixes" />
              ) : visibleMixes.length === 0 ? (
                <WorkspaceState
                  description="Create a reusable mix here and reference it from the 4R fertilizer applications workflow."
                  title="No fertilizer mixes yet"
                />
              ) : (
                <div className="grid gap-5 md:grid-cols-2">
                  {visibleMixes.map((mix) => (
                    <FertilizerMixCard key={mix.id} mix={mix} />
                  ))}
                </div>
              )}
            </div>

            <IngredientLibraryTable
              ingredients={ingredients}
              onCreate={() => openIngredientEditor(null)}
              onDelete={setIngredientDeleteTarget}
              onEdit={openIngredientEditor}
            />
          </div>
        </section>
      </div>

      <IngredientEditorModal
        formState={ingredientEditorState}
        onChange={handleChangeIngredientEditor}
        onClose={closeIngredientEditor}
        onSave={handleSaveIngredientEditor}
        open={ingredientEditor.open}
        saving={ingredientSaving}
        saveDisabled={isIngredientEditorSaveDisabled}
        title={ingredientEditor.id ? "Edit Ingredient" : "Create Ingredient"}
      />
      <DeleteResourceModal
        title="Delete Ingredient"
        description={`Are you sure you wish to permanently delete the ${
          ingredientDeleteTarget?.name || "selected"
        } ingredient?`}
        open={Boolean(ingredientDeleteTarget)}
        handleClose={() => setIngredientDeleteTarget(null)}
        handleDelete={handleDeleteIngredient}
        resourceId={ingredientDeleteTarget?.id}
      />
    </Page>
  );
}
